import "./style.scss";

import { TabContext, TabPanel } from "@mui/lab";
import { Drawer, Tab } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AlertTriangle from "assets/img/svg/alertTriangle";
import Sage from "assets/img/svg/sageIcon";
import AccountingModal from "components/AccountingModal";
import { Pending } from "components/Management/ModalContent";
import ModalBox from "components/Modal";
import BankTransferModal from "components/shared/BankTransferModal";
import type { Action } from "components/shared/ButtonMenu";
import ButtonMenu from "components/shared/ButtonMenu";
import ControlFilter from "components/shared/ControlFilter";
import type { IDateRangeShortcut } from "components/shared/DateRangeSelectors";
import DateRangeSelectors from "components/shared/DateRangeSelectors";
import ExpandedHelpdeskIcon from "components/shared/ExpandedHelpdeskIcon";
import PaymentFilter from "components/shared/ManagementGeneralFilter/PaymentFilter";
import PaymentExportFlow from "components/shared/PaymentExport";
import SageHistoryModal from "components/shared/PaymentExport/SageHistoryModal";
import SmallSearchBar from "components/shared/SmallSearchBar";
import type { IStatusRule } from "components/shared/StatusChip";
import StyledButton from "components/shared/StyledButton";
import { StyledTabs } from "components/shared/StyledTabs";
import TrackedButton from "containers/TrackedButton";
import { PortalType } from "contexts/portal.context";
import { useTracker } from "contexts/tracker";
import { formatLocaleDateString, getCleanStartorEndDay, getCurrentPeriod } from "helpers/utils";
import { isMissingBankInfo, isMissingAddress } from "helpers/paymentExportUtils";
import type { IHandleRequest, TPaidStatus } from "hooks/useAPIPagination";
import useAPIPagination from "hooks/useAPIPagination";
import { useCompanySettings } from "hooks/useCompanySettings";
import usePageTitle from "hooks/usePageTitle";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getOldestArticlePaid, resetArticles } from "store/articleStore";
import type { IPayment } from "store/paymentSlice";
import {
    fetchAccountingExport,
    fetchAccountingPreviewExport,
    fetchExportFile,
    fetchSageAccountingUploadHistory,
    fetchTaxDeclarationExport,
    getPaymentDatesBounds,
    getPaymentLots,
    resetPaymentInfo,
    setExportFileIdleStatus,
    updatePaginatedPaymentsFetchStatus,
    updateWaitingPayments,
    uploadSageAccountingToSftp,
} from "store/paymentSlice";
import type { TPeriod } from "store/periodStore";
import { fetchPeriods } from "store/periodStore";
import type { ICompany, MainPortalType } from "store/settingsStore";
import { fetchCompanyInfo } from "store/settingsStore";
import type { RootState } from "store/store";
import { useAppDispatch, useAppSelector } from "store/store";
import { EPortalType } from "store/types";
import type { IDatesRange } from "types";
import { EPaymentStatus } from "types";

import { BeneficiaryPickerFilter } from "./BeneficiaryPickerFilter/BeneficiaryPickerFilter";
import PaymentDrawerContent from "./DrawerContent";
import AccountingExport from "./ModalContent/AccountingExport";
import { TaxDeclarationExport } from "./ModalContent/TaxDeclarationExport/TaxDeclarationExport";
import PaymentLotsTable from "./PaymentLotsTable";
import PaymentTable from "./PaymentTable";
import { EPaymentBeneficiaryFilter } from "./types";
import {
    buildBookAutoDatesRangeFromPeriods,
    convertNumberToPaymentStatus,
    isPaidWithIBAN,
    resolveDefaultPaymentFilterEndDate,
    updatePaymentPageData,
} from "./utils";

/* eslint-disable */
export enum EPaymentTableStatus {
    CANCELLED = 0,
    TO_PAY = 1,
    PARTIALLY_PAID = 1,
    PAID = 2,
}

export const formatDatesChosen = (datesRange: IDatesRange | undefined, short = false) => {
    if (!datesRange) return "";
    return `${formatLocaleDateString({
        date: datesRange?.startDate,
        options: { numeric: short, timeZone: "UTC" },
    })} - ${formatLocaleDateString({
        date: datesRange.endDate,
        options: { numeric: short, timeZone: "UTC" },
    })}`;
};

export enum EPaymentPageTabs {
    WAITING = "0",
    LOTS = "1",
    HISTORY = "2",
}

export interface IPaymentPageData {
    dateRange?: { startDate: string; endDate: string };
    paymentLot?: number | "all";
    paymentStatus?: TPaidStatus;
    tabsValue?: EPaymentPageTabs;
}

const Payment = () => {
    const navigate = useNavigate();
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const portalType = useContext(PortalType);
    const { getCompanySettings, isCompany, countryFeatures, internationalBankingEnabled, usesCegidGect } =
        useCompanySettings();

    const accountingGroupMethod = usesCegidGect ? "term" : getCompanySettings("preferred_accounting_group_method");
    const usesBeneficiaryProfileCompleteCheck = getCompanySettings("uses_beneficiary_profile_complete_check");
    const accountingSoftware = getCompanySettings("accounting_software");

    // IMPORTANT:Only allow Sage and Arte companies to upload Sage accounting file (for the moment)
    const isSageEnabled = ["sage", "arte"].includes(accountingSoftware) && isCompany["arte"];

    const paymentExportSteps =
        countryFeatures.supportedPaymentFormatIds.length > 1
            ? [
                  t("PaymentModal.MethodStep"),
                  t("PaymentModal.PreppingStep"),
                  t("PaymentModal.FormatStep"),
                  t("PaymentModal.PaymentStep"),
              ]
            : undefined; // undefined → BankTransferModal uses its default 3 steps

    const initialPaymentDetails = { payment: null, show: false };

    const tracker = useTracker();

    const [paymentDetails, setPaymentDetails] = useState<{ payment: IPayment | null; show: boolean }>(
        initialPaymentDetails,
    );

    const {
        payments,
        exportFileStatus,
        paymentsStatus,
        paymentLotsStatus,
        paymentLotsOptions,
        oldestPaymentMade,
        newestPaymentMade,
        paymentDatesBoundsStatus,
    } = useAppSelector((state) => state.payment);
    const [modalType, setModalType] = useState("");
    const [showSageHistory, setShowSageHistory] = useState(false);
    usePageTitle(t("Payment.Heading"));
    const { sageUploadHistoryStatus } = useAppSelector((state) => state.payment);

    const { periods, periodsDateRange } = useAppSelector((state) => state.period);
    const { oldestArticlePaidDate } = useAppSelector((state) => state.article);
    const [currentPeriod, setCurrentPeriod] = useState<TPeriod | undefined>(getCurrentPeriod(periods));
    const companySettings = useSelector((state: RootState) => state.settings.companySettings) as ICompany;

    const paymentPageData: IPaymentPageData = JSON.parse(sessionStorage.getItem("payment_page_data") ?? "{}");
    const [statusFilter, setStatusFilter] = useState<TPaidStatus>(paymentPageData.paymentStatus ?? "all");
    const [tabsValue, setTabsValue] = useState(paymentPageData.tabsValue ?? "0");

    const [beneficiaryFilter, setBeneficiaryFilter] = useState<EPaymentBeneficiaryFilter>(
        EPaymentBeneficiaryFilter.ALL,
    );

    const isBookCompany = portalType === EPortalType.BOOK;

    /**
     * Date of first payment made.
     * As we have some clients whose first payment is older than start of first period :)
     */
    const firstPaymentDate = useMemo(() => {
        return oldestPaymentMade ? getCleanStartorEndDay(oldestPaymentMade) : getCleanStartorEndDay(new Date());
    }, [oldestPaymentMade]);

    const beneficiaryFiltersOptions = useMemo(() => {
        return [
            {
                value: EPaymentBeneficiaryFilter.ALL,
                label: t("Payment.BeneficiaryStatusFilter.All") as string,
            },
            {
                value: EPaymentBeneficiaryFilter.COMPLETED,
                label: t("Payment.BeneficiaryStatusFilter.Completed") as string,
            },
        ];
    }, [t]);

    /**
     * Automatic book range: first period (or first payment) through today or the newest payment.
     * The to-pay tab always uses this. It has no date control, so a range saved from Historique must not hide rows.
     */
    const bookDatesRange = useMemo((): IDatesRange | undefined => {
        if (!isBookCompany || !periods.length) return undefined;
        return buildBookAutoDatesRangeFromPeriods(periodsDateRange, firstPaymentDate, newestPaymentMade);
    }, [isBookCompany, periods.length, periodsDateRange, firstPaymentDate, newestPaymentMade]);

    const savedPaymentDateRange = paymentPageData.dateRange;

    /**
     * Historique date picker: the range saved in this tab, otherwise the automatic range.
     */
    const defaultPaymentRange = useMemo(() => {
        if (!isBookCompany || !currentPeriod) return;
        if (savedPaymentDateRange?.startDate && savedPaymentDateRange?.endDate) {
            return {
                startDate: getCleanStartorEndDay(savedPaymentDateRange.startDate),
                endDate: getCleanStartorEndDay(savedPaymentDateRange.endDate, { time: "end" }),
            };
        }

        return bookDatesRange;
    }, [
        isBookCompany,
        currentPeriod,
        savedPaymentDateRange?.startDate,
        savedPaymentDateRange?.endDate,
        bookDatesRange,
    ]);

    const isToPayTab = tabsValue === EPaymentPageTabs.WAITING;
    const activeBookDatesRange = isToPayTab ? bookDatesRange : defaultPaymentRange;

    const [currentDates, setCurrentDates] = useState<IDatesRange | undefined>(activeBookDatesRange);

    useEffect(() => {
        if (tracker) {
            const buttonName =
                tabsValue === EPaymentPageTabs.WAITING
                    ? "payments-to-be-paid"
                    : tabsValue === EPaymentPageTabs.LOTS
                      ? "payments-batch"
                      : tabsValue === EPaymentPageTabs.HISTORY
                        ? "payments-all"
                        : null;

            if (buttonName) {
                tracker.trackEvent("Button clicked", {
                    button: buttonName,
                });
            }
        }
    }, [tabsValue, tracker]);

    // Fetch Sage accounting upload history once when page loads and Sage is enabled
    useEffect(() => {
        if (isSageEnabled && sageUploadHistoryStatus === "idle") {
            dispatch(fetchSageAccountingUploadHistory());
        }
    }, [isSageEnabled, dispatch]);

    const apiPaginationType = () => {
        if (isBookCompany) {
            switch (tabsValue) {
                case EPaymentPageTabs.WAITING:
                    return "paymentsWaiting";
                case EPaymentPageTabs.HISTORY:
                    return "paymentsHistory";
                case EPaymentPageTabs.LOTS:
                    return "paymentLots";
            }
        }
        return "paymentsPress";
    };

    const {
        query,
        setQuery,
        handleQuery,
        count,
        page,
        limitPagination,
        sortBy,
        sortOrder,
        handleChangePage,
        handleChangeLimit,
        handleSort,
        requestStatus,
        paymentLot,
        setPaymentLot,
        setBeneficiaryIds,
        beneficiaryIds,
        paidStatus,
        setPaidStatus,
        setOffsetPagination,
        setDates,
        handleRequest,
        setLimitPagination,
    } = useAPIPagination({
        type: apiPaginationType(),
        sortOrder: "desc",
        defaultData: {
            dates: activeBookDatesRange,
            paymentLot: paymentPageData.paymentLot !== "all" ? paymentPageData.paymentLot : undefined,
        },
    });

    // handleRequest from useAPIPagination is a new function each render; putting it in useEffect deps would re-run
    // the effect every time. The ref always points at the latest handleRequest so the press-portal sync effect can
    // depend only on pressDatesRange and avoid stale closures without listing handleRequest.
    const handleRequestRef = useRef(handleRequest);
    handleRequestRef.current = handleRequest;

    const [filterType, setFilterType] = useState<"dates" | "lots">("dates");
    const [filterValue, setFilterValue] = useState(query ?? "");

    const APIData = {
        count,
        page,
        limitPagination,
        sortBy,
        sortOrder,
        handleChangePage,
        handleChangeLimit,
        handleSort,
        requestStatus,
        setOffsetPagination,
        setLimitPagination,
    };

    /** Picker bounds: book = automatic range; press = oldest article + payment bounds. */
    const pressDatesRange = useMemo((): IDatesRange | undefined => {
        if (isBookCompany || !oldestArticlePaidDate) return undefined;
        const startDate = getCleanStartorEndDay(oldestArticlePaidDate);
        const endOfToday = getCleanStartorEndDay(new Date(), { time: "end" });
        const endDate = resolveDefaultPaymentFilterEndDate(endOfToday, newestPaymentMade);
        return { startDate, endDate };
    }, [isBookCompany, oldestArticlePaidDate, newestPaymentMade]);

    const datesRange = isBookCompany ? bookDatesRange : pressDatesRange;

    useEffect(() => {
        if (isBookCompany || !pressDatesRange) return;
        setCurrentDates(pressDatesRange);
        handleRequestRef.current({ defaultType: "paymentsPress", extraOptions: { dates: pressDatesRange } });
    }, [isBookCompany, pressDatesRange]);

    /**
     * Shortcuts for dates selector.
     */
    const rangeShortcuts = useCallback((): IDateRangeShortcut[] => {
        if (!periods) return [];
        const activePeriods = periods.filter((item) => item.isActive);
        const lastClosedPeriods = periods.filter((item) => item.isActive === 0).slice(0, 3);

        const range: IDateRangeShortcut[] = [];
        [...activePeriods, ...lastClosedPeriods].forEach((period) => {
            const dates = {
                startDate: getCleanStartorEndDay(period.periodStart),
                endDate: getCleanStartorEndDay(period.periodEnd, { time: "end" }),
            };
            range.push({
                rangeKey: period.id,
                label: `${formatLocaleDateString({
                    date: dates.startDate,
                    options: {
                        numeric: true,
                        timeZone: null,
                    },
                })} - ${formatLocaleDateString({
                    date: dates.endDate,
                    options: {
                        numeric: true,
                        timeZone: null,
                    },
                })}`,
                dates,
                open: !!period.isActive,
            });
        });
        return range;
    }, [periods]);

    const handleChangeTabsValue = (
        newValue: string,
        paymentLotId?: number,
        requestOverrides?: Partial<IHandleRequest>,
    ) => {
        setTabsValue(newValue);
        updatePaymentPageData("tabsValue", newValue);

        switch (newValue) {
            case EPaymentPageTabs.WAITING:
                if (bookDatesRange) {
                    setCurrentDates(bookDatesRange);
                    setDates(bookDatesRange);
                }
                handleRequest({
                    defaultType: "paymentsWaiting",
                    extraOptions: { paidStatus: "topay", dates: bookDatesRange },
                });
                break;
            case EPaymentPageTabs.LOTS:
                handleRequest({ defaultType: "paymentLots" });
                break;
            case EPaymentPageTabs.HISTORY:
                if (defaultPaymentRange) {
                    setCurrentDates(defaultPaymentRange);
                    setDates(defaultPaymentRange);
                }
                handleRequest({
                    defaultType: "paymentsHistory",
                    extraOptions: {
                        paymentLot: paymentLotId,
                        ...(defaultPaymentRange ? { dates: defaultPaymentRange } : {}),
                    },
                    ...requestOverrides,
                });
                break;
        }
    };

    const handleExportFiles = useCallback(
        async (
            executionDate: Date,
            downloadMode: "preview" | "pay",
            paymentLotId?: number | null,
            formatId?: string,
            fileNumber?: number,
        ) => {
            if (!currentDates) return;
            const exportParams = {
                startDate: new Date(currentDates.startDate).toLocaleDateString("en-CA"),
                endDate: new Date().toLocaleDateString("en-CA"),
                executionDate,
                paymentLotId,
                formatId,
                fileNumber,
            };
            const response = await dispatch(fetchExportFile({ params: exportParams, downloadMode }));

            if (response.meta.requestStatus === "fulfilled") {
                handleRequestRef.current({ defaultType: "paymentsHistory" });
                if (downloadMode !== "pay") return response;
                if (!!paymentLotId) {
                    dispatch(updateWaitingPayments(paymentLotId));
                    handleChangeTabsValue(EPaymentPageTabs.LOTS);
                } else {
                    // press portal doesn't have payment lots
                    if (!datesRange) return;
                    handleRequest({ defaultType: "paymentsWaiting", extraOptions: { dates: datesRange } });
                }
            }

            return response;
        },
        [currentDates, dispatch],
    );

    const handleSageUpload = useCallback(
        async (executionDate: Date, paymentLotId?: number | null, formatId?: string) => {
            if (!currentDates) return;
            const params = {
                startDate: new Date(currentDates.startDate).toLocaleDateString("en-CA"),
                endDate: new Date().toLocaleDateString("en-CA"),
                executionDate: executionDate.toISOString(),
                paymentLotId,
                formatId,
            };
            return dispatch(uploadSageAccountingToSftp(params));
        },
        [currentDates, dispatch],
    );

    const handleAccountingExport = useCallback(
        (data: Record<string, any> = {}) => {
            if (!currentDates) return;
            const exportParams = {
                startDate: new Date(currentDates.startDate).toLocaleDateString("en-CA"),
                endDate: new Date(currentDates.endDate).toLocaleDateString("en-CA"),
            };

            dispatch(fetchAccountingExport({ ...exportParams, ...data })).then((res) => {
                if (res.meta.requestStatus === "fulfilled") {
                    setModalType("");
                    handleRequestRef.current({ defaultType: "paymentsHistory" });
                }
            });
        },
        [currentDates, dispatch],
    );

    // Softwares that produce a specialized format file alongside the xlsx → response is a ZIP.
    // Otherwise (pennylane, lunii, unset) the backend streams a single xlsx directly.
    const hasSpecializedAccountingFile = ["sage", "cegid", "cegid_gect", "arte", "cogilog"].includes(
        accountingSoftware,
    );

    const handleAccountingPreviewExport = useCallback(() => {
        if (!currentDates) return;
        dispatch(
            fetchAccountingPreviewExport({
                startDate: new Date(currentDates.startDate).toLocaleDateString("en-CA"),
                endDate: new Date(currentDates.endDate).toLocaleDateString("en-CA"),
                hasSpecializedFile: hasSpecializedAccountingFile,
            }),
        );
    }, [currentDates, dispatch, hasSpecializedAccountingFile]);

    const handleTaxDeclarationExport = useCallback(
        (selectedBeneficiaryIds: number[]) => {
            if (!currentDates) return;

            dispatch(
                fetchTaxDeclarationExport({
                    // Send the local calendar day (YYYY-MM-DD) — toISOString would shift
                    // to a UTC day matching neither the user's pick nor the sibling routes.
                    startDate: new Date(currentDates.startDate).toLocaleDateString("en-CA"),
                    endDate: new Date(currentDates.endDate).toLocaleDateString("en-CA"),
                    beneficiaryIds: selectedBeneficiaryIds,
                }),
            ).then((res) => {
                if (res.meta.requestStatus === "fulfilled") {
                    setModalType("");
                }
            });
        },
        [currentDates, dispatch],
    );

    const showTaxDeclarationExport = portalType !== EPortalType.BENEFICIARY && portalType !== EPortalType.URSSAF;
    const areExportsDisabled = paidStatus !== "paid" && paidStatus !== "all";

    /**
     * Triggers new data fetch when dates change.
     */
    const handleDatesChange = (updatedDates: IDatesRange | undefined) => {
        if (!updatedDates) return;
        setCurrentDates(updatedDates);
        setDates(updatedDates);
        // Have to convert date to string here, otherwise when doing the stringify it uses local time and changes actual date passed (for instance January 1st at 00:00 becomes Dec 31st)
        paymentPageData["dateRange"] = {
            startDate: updatedDates.startDate.toLocaleDateString("en-CA"),
            endDate: updatedDates.endDate.toLocaleDateString("en-CA"),
        };
        sessionStorage.setItem("payment_page_data", JSON.stringify(paymentPageData));
    };

    /**
     * Fetches periods data in case activePeriod doesn't exist.
     */
    useEffect(() => {
        if (!periods.length && portalType === EPortalType.BOOK) {
            dispatch(fetchPeriods()).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setCurrentPeriod(getCurrentPeriod(resp.payload));
                }
            });
        }
    }, [periods]);

    /**
     * Gets date of oldest article paid to properly set default dates on range.
     */
    useEffect(() => {
        if (portalType !== EPortalType.BOOK && !oldestArticlePaidDate) {
            dispatch(getOldestArticlePaid());
        }
    }, [oldestArticlePaidDate]);

    /**
     * Load min/max payment dates so default range includes future-dated payments beyond the last period (book and press).
     */
    useEffect(() => {
        if (paymentDatesBoundsStatus !== "idle") return;
        dispatch(getPaymentDatesBounds());
    }, [paymentDatesBoundsStatus, dispatch]);

    /**
     * Book: push the tab's date range into the list and the exports.
     * To-pay uses the automatic range (today / newest payment). Historique keeps the saved range.
     * Runs on first load, when payment-date bounds arrive, and when the active tab changes.
     */
    useEffect(() => {
        if (portalType !== EPortalType.BOOK) return;
        if (tabsValue === EPaymentPageTabs.LOTS) return;
        if (!activeBookDatesRange) return;
        const syncOnInitialLoad = paymentsStatus === "idle";
        const syncAfterBoundsLoaded = paymentDatesBoundsStatus === "succeeded";
        if (!syncOnInitialLoad && !syncAfterBoundsLoaded) return;
        setCurrentDates(activeBookDatesRange);
        setDates(activeBookDatesRange);
    }, [activeBookDatesRange, paymentsStatus, paymentDatesBoundsStatus, portalType, setDates, tabsValue]);

    useEffect(() => {
        if (isBookCompany && tabsValue === EPaymentPageTabs.HISTORY && paymentLotsStatus === "idle") {
            const savedLot = paymentPageData.paymentLot;
            const savedPaymentStatus = paymentPageData.paymentLot;
            dispatch(
                getPaymentLots({
                    options: {
                        limit: 10,
                        sortOrder: "desc",
                        paymentLot: savedLot && savedLot !== "all" ? savedLot : undefined,
                        paidStatus:
                            savedPaymentStatus && savedPaymentStatus !== "all"
                                ? convertNumberToPaymentStatus(savedPaymentStatus)
                                : "all",
                    },
                }),
            );
        }

        if (paymentPageData.paymentLot !== "all") {
            setFilterType("lots");
        }
    }, []);

    useEffect(() => {
        if (exportFileStatus === "failed") {
            dispatch(setExportFileIdleStatus());
        }
    }, [dispatch, exportFileStatus]);

    useEffect(() => {
        if (companySettings === undefined) dispatch(fetchCompanyInfo());
    }, [companySettings, dispatch]);

    /**
     * Filters payments to show only those that can be paid by bank transfer.
     * This includes:
     * - Payments with valid bank information (IBAN) OR no bank information
     * - Payments that have remaining amount to pay
     * - Payments where beneficiary profile is complete (if the check is enabled)
     */
    const paymentsForBankTransfer = payments.filter((payment) => {
        const hasValidBankInfo = isPaidWithIBAN(payment);
        const hasRemainingToPay = payment.netRemainingToPay > 0;
        const isBeneficiaryProfileComplete =
            !usesBeneficiaryProfileCompleteCheck || payment.beneficiary.isProfileComplete;

        return hasValidBankInfo && hasRemainingToPay && isBeneficiaryProfileComplete;
    });

    const beneficiariesWithNoBankInfo = paymentsForBankTransfer.filter((item: IPayment) =>
        isMissingBankInfo(item, { internationalBankingEnabled, bankingFields: countryFeatures.bankingFields }),
    );

    const isSepaFormat = countryFeatures.supportedPaymentFormatIds.some(
        (id) => id === "ISO20022_PAIN001" || id === "ISO20022_PAIN001_INTL",
    );
    const beneficiariesWithNoAddress =
        internationalBankingEnabled && isSepaFormat
            ? payments.filter(
                  (item: IPayment) =>
                      item.beneficiary?.bankingStatus === "foreign_international" && isMissingAddress(item),
              )
            : [];

    const paymentStatusRules = (): Record<number, IStatusRule> => {
        if (portalType === EPortalType.BOOK) {
            return {
                [EPaymentTableStatus.TO_PAY]: {
                    color: "orange",
                    text: t("Filters.ForTransfer"),
                },
                [EPaymentTableStatus.PAID]: {
                    color: "green",
                    text: t("Management.Paid"),
                },
                [EPaymentTableStatus.CANCELLED]: {
                    color: "blue",
                    text: t("PartialPayment.PaymentPage.Cancelled"),
                },
            };
        } else {
            return {
                [EPaymentTableStatus.TO_PAY]: {
                    color: "orange",
                    text: t("Payment.ToPay"),
                },
                [EPaymentTableStatus.PAID]: {
                    color: "green",
                    text: t("Payment.TotallyPaid"),
                },
            };
        }
    };

    const helpdeskPayment: Record<MainPortalType, string> = {
        press: "https://presse-helpdesk.crealo.app/fr/article/comprendre-la-page-paiement-f3p35i/",
        audiovisual: "https://presse-helpdesk.crealo.app/fr/article/comprendre-la-page-paiement-f3p35i/",
        book: "https://helpdesk.crealo.app/fr/article/lecran-de-paiements-1wucws4/?bust=1740763649372",
    };

    const handleChangeFilterValue = (e: React.ChangeEvent<HTMLInputElement>) => {
        handleQuery(e);
        setFilterValue(e.target.value);
    };

    const lotFilterOptions = useMemo(() => {
        if (!paymentLotsOptions) return [];
        const realLots = paymentLotsOptions.map((item) => ({ value: item, label: `#${item}` })).reverse();
        return [{ value: "all", label: t("Filters.AllMasculine") }, ...realLots];
    }, [paymentLotsOptions]);

    const handleLotFilterChange = (value: number | "all", from = "filter") => {
        setPaymentLot(value);
        if (from === "filter") {
            setFilterType("lots");
        }
        updatePaymentPageData("paymentLot", value);
    };

    /**
     * Changes tab to History and selects lot to filter associated payments.
     * Clears the lots-tab search query so it is not sent to the history endpoint.
     */
    const handlePaymentLotFilter = (lotId: number | undefined) => {
        if (!lotId) return;
        dispatch(updatePaginatedPaymentsFetchStatus("pending"));
        // Clear filters when filtering by lot to avoid sending it to the history endpoint.
        setQuery("");
        setFilterValue("");
        resetPagination();
        handleChangeTabsValue(EPaymentPageTabs.HISTORY, lotId, { savedQuery: "" });
        handleLotFilterChange(lotId);
        updatePaymentPageData("paymentLot", lotId);
    };

    const handlePaymentStatusFilter = (status: TPaidStatus) => {
        setStatusFilter(status);
    };

    const resetPagination = () => {
        handleChangePage(undefined, 0);
        setOffsetPagination(0);
    };

    const filterTypeOptions = [
        { value: "dates", label: t("Filters.Dates") },
        { value: "lots", label: t("Filters.PaymentLots") },
    ];

    const handleFilterTypeChange = (type: "dates" | "lots") => {
        setFilterType(type);
        if (type === "dates") {
            handleLotFilterChange("all", "switch");
        } else {
            handleDatesChange(defaultPaymentRange);
        }
    };

    const handleShowPaymentDetails = useCallback((payment: IPayment) => {
        setPaymentDetails({ payment, show: true });
    }, []);

    const handleClosePaymentDetails = () => {
        setPaymentDetails((prev) => ({ ...prev, show: false }));
        /**
         * Timeout to allow drawer animation to complete before resetting data
         * @note MUI drawer uses 'open' prop for animation
         */
        setTimeout(() => {
            setPaymentDetails(initialPaymentDetails);
        }, 200);
        // reset store payment info to avoid blink previous data when opening drawer
        if (portalType !== EPortalType.BOOK) {
            dispatch(resetArticles());
        }
        dispatch(resetPaymentInfo());
    };

    return (
        <>
            <ModalBox open={exportFileStatus === "pending"} onClose={() => {}} showCloseButton={false}>
                <Pending phrase={t("Payment.ExportingFile")} />
            </ModalBox>
            <BankTransferModal
                open={modalType === "payment"}
                onClose={() => setModalType("")}
                mainTitle={t("PaymentModal.PayBeneficiaires")}
                steps={paymentExportSteps}
            >
                <PaymentExportFlow
                    payments={paymentsForBankTransfer}
                    beneficiariesWithNoBankInfo={beneficiariesWithNoBankInfo}
                    beneficiariesWithNoAddress={beneficiariesWithNoAddress}
                    hasIncompleteBeneficiaries={
                        usesBeneficiaryProfileCompleteCheck
                            ? // filter on unfiltered payments to check if any beneficiary is incomplete
                              payments.some((item: IPayment) => !item.beneficiary?.isProfileComplete)
                            : undefined
                    }
                    handleDownload={handleExportFiles}
                    handleSageUpload={isSageEnabled ? handleSageUpload : undefined}
                    isSageEnabled={isSageEnabled}
                    close={() => setModalType("")}
                    company={companySettings}
                    supportedPaymentFormatIds={countryFeatures.supportedPaymentFormatIds}
                />
            </BankTransferModal>
            <ModalBox
                open={modalType === "accounting"}
                onClose={() => setModalType("")}
                showCloseButton={true}
                mainTitle={t("Payment.ExportAccountingYear", { year: "" })}
                subTitle={t("PartialPayment.PaymentPage.AccountingSubtitle", { date: formatDatesChosen(currentDates) })}
            >
                {accountingGroupMethod === "term" ? (
                    <AccountingModal origin="payment" datesRange={currentDates} onSubmit={handleAccountingExport} />
                ) : (
                    <AccountingExport handleDownload={handleAccountingExport} datesRange={currentDates} />
                )}
            </ModalBox>
            <ModalBox
                open={modalType === "taxDeclaration"}
                onClose={() => setModalType("")}
                showCloseButton={true}
                mainTitle={t("Payment.TaxDeclarationExport.ButtonTitle")}
                subTitle={t("PartialPayment.PaymentPage.AccountingSubtitle", { date: formatDatesChosen(currentDates) })}
            >
                <TaxDeclarationExport
                    dateRange={formatDatesChosen(currentDates)}
                    handleDownload={handleTaxDeclarationExport}
                    initialBeneficiaryIds={beneficiaryIds}
                />
            </ModalBox>

            <div className="payment-wrapper" style={{ gap: isBookCompany ? "1rem" : "2rem" }}>
                <Drawer anchor="right" open={paymentDetails.show} onClose={handleClosePaymentDetails}>
                    {paymentDetails.payment && (
                        <PaymentDrawerContent
                            close={handleClosePaymentDetails}
                            payment={paymentDetails.payment}
                            datesRange={datesRange}
                        />
                    )}
                </Drawer>
                <header className="payment-header">
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <h1 className="payment-heading">{t("Payment.Heading")}</h1>
                        <ExpandedHelpdeskIcon
                            link={helpdeskPayment[portalType]}
                            text={t("Documentation.PaymentPage")}
                        />
                    </div>
                    {!isBookCompany && (
                        <div className="export-button-wrapper" onClick={(e) => e.stopPropagation()}>
                            {isSageEnabled && (
                                <TrackedButton action="payments-sage-history">
                                    <StyledButton onClick={() => setShowSageHistory(true)} buttonType="outlined">
                                        <Sage className="sage-file-icon" style={{ marginRight: "0.5rem" }} />
                                        {t("Payment.SageHistoryButton")}
                                    </StyledButton>
                                </TrackedButton>
                            )}
                            <PaymentDownloadDocumentDropdown
                                onSelectBankTransfer={() => setModalType("payment")}
                                onSelectAccounting={handleAccountingPreviewExport}
                                accountingSoftware={accountingSoftware}
                            />
                            <PaymentDownloadDropdown
                                setModalType={setModalType}
                                showTaxDeclarationExport={showTaxDeclarationExport}
                                exportDisabled={areExportsDisabled}
                            />
                        </div>
                    )}
                </header>

                <main className="payment-main" style={{}}>
                    {!isBookCompany && (
                        <div className="filter-wrapper">
                            <SmallSearchBar
                                filter={filterValue}
                                onSearch={handleChangeFilterValue}
                                placeholder={t("Payment.SearchPlaceholder")}
                            />
                            {usesBeneficiaryProfileCompleteCheck ? (
                                <ControlFilter
                                    filterLabel={t("Payment.BeneficiaryStatusFilter.Title")}
                                    controlValue={beneficiaryFilter}
                                    onValueChange={setBeneficiaryFilter}
                                    filterOptions={beneficiaryFiltersOptions}
                                />
                            ) : null}
                            <DateRangeSelectors
                                label={t("PartialPayment.PaymentsBetween")}
                                dateRange={datesRange}
                                defaultValues={datesRange}
                                handleChanges={handleDatesChange}
                                loading={!datesRange}
                                rangeShortcuts={rangeShortcuts()}
                            />
                        </div>
                    )}
                    {!(
                        (companySettings.iban && companySettings.bankAccountName) ||
                        (companySettings.bankingMetadata && Object.keys(companySettings.bankingMetadata).length > 0)
                    ) &&
                        (isBookCompany ? tabsValue === EPaymentPageTabs.WAITING : true) && (
                            <div className="company-bank-export-noInfo">
                                <span className="company-bank-export-noInfo-text red">
                                    <AlertTriangle />
                                    {t("Payment.FillCompanyBankInfoFirst1")}
                                    <button className="settings-btn" onClick={() => navigate("/settings/company")}>
                                        Paramètres
                                    </button>
                                    {t("Payment.FillCompanyBankInfoFirst2")}
                                </span>
                            </div>
                        )}
                    {isBookCompany ? (
                        <TabContext value={tabsValue}>
                            <div className={"tabs-bar"}>
                                <StyledTabs
                                    value={tabsValue}
                                    onChange={(e, value) => handleChangeTabsValue(value)}
                                    className="payment-tabs"
                                >
                                    <Tab
                                        label={t("PartialPayment.PaymentPage.ToPayTab")}
                                        value={EPaymentPageTabs.WAITING}
                                        style={{ paddingInline: "0px" }}
                                    />
                                    <Tab
                                        label={t("PartialPayment.PaymentPage.LotTab")}
                                        value={EPaymentPageTabs.LOTS}
                                        style={{ paddingInline: "0px" }}
                                    />
                                    <Tab
                                        label={t("PartialPayment.PaymentPage.HistoryTab")}
                                        value={EPaymentPageTabs.HISTORY}
                                        style={{ paddingInline: "0px" }}
                                    />
                                </StyledTabs>
                                {tabsValue === EPaymentPageTabs.WAITING ? (
                                    <div className="export-button-wrapper">
                                        {isSageEnabled && (
                                            <TrackedButton action="payments-sage-history">
                                                <StyledButton
                                                    onClick={() => setShowSageHistory(true)}
                                                    buttonType="outlined"
                                                >
                                                    <Sage
                                                        className="sage-file-icon"
                                                        style={{ marginRight: "0.5rem" }}
                                                    />
                                                    {t("Payment.SageHistoryButton")}
                                                </StyledButton>
                                            </TrackedButton>
                                        )}
                                        <TrackedButton action="payments-export">
                                            <PaymentDownloadDocumentDropdown
                                                onSelectBankTransfer={() => setModalType("payment")}
                                                onSelectAccounting={handleAccountingPreviewExport}
                                                accountingSoftware={accountingSoftware}
                                                disabled={
                                                    payments.filter((item) => item.paidStatus === EPaymentStatus.TO_PAY)
                                                        .length === 0
                                                }
                                            />
                                        </TrackedButton>
                                    </div>
                                ) : (
                                    tabsValue === EPaymentPageTabs.HISTORY && (
                                        <PaymentDownloadDropdown
                                            setModalType={setModalType}
                                            showTaxDeclarationExport={showTaxDeclarationExport}
                                            exportDisabled={areExportsDisabled}
                                        />
                                    )
                                )}
                            </div>
                            <div className="export-button-wrapper" onClick={(e) => e.stopPropagation()}>
                                {tabsValue === EPaymentPageTabs.HISTORY ? (
                                    <div className="w-full">
                                        <BeneficiaryPickerFilter value={beneficiaryIds} onChange={setBeneficiaryIds} />
                                    </div>
                                ) : (
                                    <SmallSearchBar
                                        filter={filterValue}
                                        onSearch={handleChangeFilterValue}
                                        placeholder={t("Payment.SearchPlaceholder")}
                                    />
                                )}

                                {tabsValue !== EPaymentPageTabs.WAITING && (
                                    <PaymentFilter
                                        paidStatus={paidStatus}
                                        setPaidStatus={setPaidStatus}
                                        resetPagination={resetPagination}
                                        selector
                                        onChange={handlePaymentStatusFilter}
                                        allowCancelledStatus
                                        highlight={paidStatus !== "all"}
                                    />
                                )}

                                {tabsValue === EPaymentPageTabs.LOTS && (
                                    <ControlFilter
                                        filterLabel={t("PartialPayment.LotsTable.PaymentLot")}
                                        filterOptions={lotFilterOptions}
                                        controlValue={paymentLot ?? paymentPageData.paymentLot}
                                        controlLabel={
                                            lotFilterOptions.find(
                                                (item) => item.value === (paymentLot ?? paymentPageData.paymentLot),
                                            )?.label ?? ""
                                        }
                                        onValueChange={handleLotFilterChange}
                                        selector
                                        menuMaxHeight={"300px"}
                                        highlight={!!paymentPageData.paymentLot && paymentPageData.paymentLot !== "all"}
                                    />
                                )}
                                {tabsValue === EPaymentPageTabs.HISTORY && (
                                    <div className="payments-filter-wrapper" onClick={(e) => e.stopPropagation()}>
                                        <ControlFilter
                                            key={"filter-type"}
                                            filterLabel={t("Filters.FilterBy")}
                                            filterOptions={filterTypeOptions}
                                            controlValue={filterType}
                                            controlLabel={
                                                filterTypeOptions.find((item) => item.value === filterType)?.label
                                            }
                                            onValueChange={handleFilterTypeChange}
                                            selector
                                        />
                                        {filterType === "dates" ? (
                                            <DateRangeSelectors
                                                label={t("PartialPayment.PaymentsBetween")}
                                                dateRange={datesRange}
                                                defaultValues={defaultPaymentRange}
                                                handleChanges={handleDatesChange}
                                                loading={!datesRange}
                                                rangeShortcuts={rangeShortcuts()}
                                            />
                                        ) : (
                                            <ControlFilter
                                                filterLabel={t("PartialPayment.LotsTable.PaymentLot")}
                                                filterOptions={lotFilterOptions}
                                                controlValue={paymentLot ?? paymentPageData.paymentLot}
                                                controlLabel={
                                                    lotFilterOptions.find(
                                                        (item) =>
                                                            item.value === (paymentLot ?? paymentPageData.paymentLot),
                                                    )?.label ?? ""
                                                }
                                                onValueChange={handleLotFilterChange}
                                                selector
                                                menuMaxHeight={"300px"}
                                                highlight={
                                                    filterType === "lots" &&
                                                    !!paymentPageData.paymentLot &&
                                                    paymentPageData.paymentLot !== "all"
                                                }
                                            />
                                        )}
                                    </div>
                                )}
                            </div>

                            <TabPanel value={EPaymentPageTabs.WAITING} style={{ padding: "0px" }}>
                                <PaymentTable
                                    type={EPortalType.BOOK}
                                    searchBarValue={filterValue}
                                    paymentStatusRules={paymentStatusRules()}
                                    handleChangeFilterValue={handleChangeFilterValue}
                                    handleShowPaymentDetails={handleShowPaymentDetails}
                                    onlyWaitingPayments
                                    APIData={APIData}
                                />
                            </TabPanel>
                            <TabPanel value={EPaymentPageTabs.LOTS} style={{ padding: "0px" }}>
                                <PaymentLotsTable
                                    allPayments={payments}
                                    paymentStatusRules={paymentStatusRules()}
                                    APIData={APIData}
                                    handlePaymentFilter={handlePaymentLotFilter}
                                    onAccountingExported={() =>
                                        handleRequestRef.current({ defaultType: "paymentsHistory" })
                                    }
                                />
                            </TabPanel>
                            <TabPanel value={EPaymentPageTabs.HISTORY} style={{ padding: "0px" }}>
                                <PaymentTable
                                    type={EPortalType.BOOK}
                                    searchBarValue={filterValue}
                                    paymentStatusRules={paymentStatusRules()}
                                    handleChangeFilterValue={handleChangeFilterValue}
                                    handleShowPaymentDetails={handleShowPaymentDetails}
                                    paymentStatusFilter={statusFilter}
                                    APIData={APIData}
                                />
                            </TabPanel>
                        </TabContext>
                    ) : (
                        <PaymentTable
                            type={portalType as MainPortalType}
                            searchBarValue={filterValue}
                            beneficiaryFilter={beneficiaryFilter}
                            paymentStatusRules={paymentStatusRules()}
                            handleChangeFilterValue={handleChangeFilterValue}
                            handleShowPaymentDetails={handleShowPaymentDetails}
                        />
                    )}
                </main>
            </div>
            <SageHistoryModal open={showSageHistory && isSageEnabled} onClose={() => setShowSageHistory(false)} />
        </>
    );
};

export const PaymentDownloadDocumentDropdown = ({
    onSelectBankTransfer,
    onSelectAccounting,
    accountingSoftware,
    disabled = false,
}: {
    onSelectBankTransfer: () => void;
    onSelectAccounting: () => void;
    accountingSoftware: string | undefined;
    disabled?: boolean;
}) => {
    const [t] = useTranslation();
    const accountingDisabled = !accountingSoftware;

    return (
        <ButtonMenu
            buttonType="primary"
            showExpandBtn={false}
            disabled={disabled}
            mainActions={{
                icon: <FileDownloadOutlinedIcon sx={{ width: "18px", height: "18px", marginRight: "6px" }} />,
                name: t("Payment.DownloadDocument"),
            }}
            actions={[
                {
                    name: t("Payment.DownloadOption.BankTransfer"),
                    action: onSelectBankTransfer,
                },
                {
                    name: t("Payment.DownloadOption.Accounting"),
                    action: onSelectAccounting,
                    disabled: accountingDisabled,
                    subtitle: accountingDisabled ? t("Payment.DownloadOption.AccountingDisabledTooltip") : undefined,
                },
            ]}
        />
    );
};

const PaymentDownloadDropdown = ({
    showTaxDeclarationExport,
    exportDisabled,
    setModalType,
}: {
    showTaxDeclarationExport: boolean;
    exportDisabled: boolean;
    setModalType: React.Dispatch<React.SetStateAction<string>>;
}) => {
    const [t] = useTranslation();

    const actions: Action[] = [
        {
            name: t("Payment.ExportAccounting"),
            action: () => setModalType("accounting"),
        },
    ];
    if (showTaxDeclarationExport) {
        actions.push({
            name: t("Payment.TaxDeclarationExport.ButtonTitle"),
            action: () => setModalType("taxDeclaration"),
        });
    }
    return (
        <ButtonMenu
            buttonType="primary"
            showExpandBtn={false}
            disabled={exportDisabled}
            tooltipText={exportDisabled ? t("Payment.ExportAccountingDisabledTooltip") : undefined}
            mainActions={{
                icon: <FileDownloadOutlinedIcon sx={{ width: "18px", height: "18px", marginRight: "6px" }} />,
                name: t("History.Download"),
            }}
            actions={actions}
        />
    );
};

export default Payment;
